// src/components/demo/DemoLekhiPage.js
import React, { useState, useCallback, useEffect } from 'react';
import AudioView from '../lekhi/AudioView';
import FlashcardView from '../lekhi/FlashcardView';
import MCQs from '../lekhi/MCQs';
import '../lekhi/LekhiTrainingPage.css';
import './DemoCelebration.css';

const DonutProgress = ({ completed, total }) => {
  const size = 72;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? (completed / total) * circumference : 0;
  const offset = circumference - progress;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgb(255, 255, 255)" strokeWidth={strokeWidth}/>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#28a745" strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: 'stroke-dashoffset 0.4s ease' }}/>
    </svg>
  );
};

const AUDIO_DATA = { audioURL: '/lcm_hcf_audio.mp3' };
const preloadAudio = new Audio('/lcm_hcf_audio.mp3');
preloadAudio.preload = 'auto';

const FLASHCARDS = [
  {
    id: 'fc1', front: 'मसावि काढताना आलेले उत्तर मूळ संख्यांपेक्षा कसे असते?',
    back: 'लहान किंवा बरोबरीचे',
    hint: 'विभाजक हा तुकडे करतो की पट वाढवतो?',
    example: '८ आणि १२ चा मसावि ४ येतो, जो दोन्हीपेक्षा लहान आहे.'
  },
  {
    id: 'fc2', front: 'लसावि काढण्यासाठी कोणते अवयव वापरले जातात?',
    back: 'सामाईक आणि असामाईक अवयवांचा गुणाकार',
    hint: 'फक्त सामाईक अवयव घेऊन लसावि निघतो का?',
    example: 'लसावि = (सामाईक अवयव) × (उरलेले सर्व अवयव)'
  }
];

const MCQ_DATA = [
  {
    id: 'mcq1',
    question: 'दिलेल्या दोन संख्यांना पूर्ण भाग जाणारी सर्वात मोठी संख्या म्हणजे काय?',
    options: { A: 'लसावि', B: 'विभाज्य', C: 'मसावि', D: 'गुणाकार' },
    correctAnswer: 'C',
    explanation: "मसावि म्हणजे 'महत्तम सामाईक विभाजक' होय, जो दिलेल्या संख्यांना भाग देणारा सर्वात मोठा तुकडा असतो."
  },
  {
    id: 'mcq2',
    question: '४ आणि १० या संख्यांचा मसावि किती?',
    options: { A: '४', B: '२', C: '२०', D: '५' },
    correctAnswer: 'B',
    explanation: "४ चे अवयव २ × २ आणि १० चे अवयव २ × ५ आहेत, यात '२' हा एकमेव सामाईक विभाजक आहे."
  }
];

const TOTAL_BLOCKS = 3;

const DemoLekhiPage = ({ onBack }) => {
  const [language, setLanguage] = useState(() =>
    localStorage.getItem('spocademy_language') || 'mr'
  );

  useEffect(() => {
    preloadAudio.load();
  }, []);
  
  const [completedBlocks, setCompletedBlocks] = useState(new Set());
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [dotState, setDotState] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);

  const t = language === 'mr'
    ? { day: 'दिवस', markComplete: 'सुरू करा', completed: '✓ पूर्ण', back: '← मागे', shabash: 'शाबास!', shabashBtn: 'पुढे' }
    : { day: 'Day', markComplete: 'Start', completed: '✓ Done', back: '← Back', shabash: 'Shabash!', shabashBtn: 'Continue' };

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
  };

  const blocks = [
    { id: 'audio1', iconType: 'learn', title: language === 'mr' ? 'शिकवणी' : 'Teaching', subtitle: language === 'mr' ? 'ऑडिओ १' : 'Audio 1', type: 'audio' },
    { id: 'flashcards', iconType: 'practice', title: language === 'mr' ? 'फ्लॅशकार्ड्स' : 'Flashcards', subtitle: `2 ${language === 'mr' ? 'कार्ड्स' : 'cards'}`, type: 'flashcards' },
    { id: 'mcq1', iconType: 'practice', title: language === 'mr' ? 'MCQs' : 'MCQs', subtitle: `2 ${language === 'mr' ? 'प्रश्न' : 'questions'}`, type: 'mcq' }
  ];

  const totalDots = 2;

  const recordItemResult = (itemId, correct) => {
    setDotState(prev => [...prev, correct]);
  };

  const playClap = () => {
    try {
      const a = new Audio('/claps.mp3');
      a.play().catch(() => {});
    } catch (e) {}
  };

  const handleBlockSubComplete = useCallback(() => {
    if (!activeBlockId) return;
    setCompletedBlocks(prev => {
      const next = new Set(prev);
      next.add(activeBlockId);
      if (next.size >= TOTAL_BLOCKS) {
        if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
        setTimeout(() => { setShowCelebration(true); playClap(); }, 600);
      }
      return next;
    });
    setActiveBlockId(null);
  }, [activeBlockId]);

  const handleBlockSubBack = () => setActiveBlockId(null);

  const handleBlockTap = (block, index) => {
    if (completedBlocks.has(block.id)) return;
    const firstUncompletedIndex = blocks.findIndex(b => !completedBlocks.has(b.id));
    if (index !== firstUncompletedIndex) return;
    setActiveBlockId(block.id);
  };

  if (activeBlockId) {
    const activeBlock = blocks.find(b => b.id === activeBlockId);
    const commonProps = { language, onComplete: handleBlockSubComplete, onBack: handleBlockSubBack };
    if (activeBlock?.type === 'audio') {
      return <AudioView audio={AUDIO_DATA} blockLabel={activeBlock.title} topicName={language === 'mr' ? 'मसावि / लसावि' : 'LCM / HCF'} {...commonProps} />;
    }
    if (activeBlock?.type === 'flashcards') {
      return <FlashcardView flashcards={FLASHCARDS} {...commonProps} />;
    }
    if (activeBlock?.type === 'mcq') {
      return <MCQs mcqs={MCQ_DATA} recordItemResult={recordItemResult} dotState={dotState} totalDots={totalDots} {...commonProps} />;
    }
  }

  const firstUncompletedIndex = blocks.findIndex(b => !completedBlocks.has(b.id));

  return (
    <div className="lekhi-training-container">
      <div className="lk-header">
        <div className="lk-header-top">
          <button className="back-btn" onClick={onBack}>{t.back}</button>
          <button className="lang-toggle-lk" onClick={handleLanguageToggle}>
            {language === 'en' ? 'मराठी' : 'EN'}
          </button>
        </div>
        <div className="lk-header-center">
          <DonutProgress completed={completedBlocks.size} total={TOTAL_BLOCKS} />
          <span className="lk-progress-text">{completedBlocks.size}/{TOTAL_BLOCKS}</span>
        </div>
        <div className="lk-header-bottom">
          <div className="lk-left">
            <div className="lk-user-name">Virat Kohli</div>
            <div className="lk-date">Day 15</div>
          </div>
          <div className="lk-day-badge">{t.day} 15</div>
        </div>
      </div>

      <div className="lk-topic-band">
        <div className="lk-topic-band-label">आजचा विषय</div>
        <div className="lk-topic-band-value">गणित - मसावि व लसावि</div>
      </div>

      <div className="lk-block-list">
        {blocks.map((block, i) => {
          const isCompleted = completedBlocks.has(block.id);
          const isActive = i === firstUncompletedIndex;
          const isLocked = !isCompleted && !isActive;
          const iconSrc = isLocked ? '/lock.svg' : (block.iconType === 'learn' ? '/learn.svg' : '/practice.svg');
          return (
            <div key={block.id} className={`lk-block-card ${isCompleted ? 'completed' : ''} ${isLocked ? 'locked' : ''}`}>
              <div className="lk-block-header">
                <div className="lk-block-icon-wrap">
                  <img src={iconSrc} alt="" className="lk-block-icon-img" />
                </div>
                <div className="lk-block-info">
                  <div className="lk-block-name">{block.title}</div>
                  <div className="lk-block-subtitle">{block.subtitle}</div>
                </div>
              </div>
              <div className="lk-block-controls">
                {isCompleted ? (
                  <div className="lk-completed-indicator">{t.completed}</div>
                ) : isLocked ? null : (
                  <button className="lk-start-btn" onClick={() => handleBlockTap(block, i)}>
                    {t.markComplete}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showCelebration && (
        <div className="demo-celebration-overlay">
          <div className="demo-confetti">
            {Array.from({ length: 40 }).map((_, i) => (
              <span key={i} className={`demo-confetti-piece piece-${i % 5}`} style={{ left: `${(i * 2.5) % 100}%`, animationDelay: `${(i % 10) * 0.15}s` }} />
            ))}
          </div>
          <div className="demo-celebration-box">
            <div className="demo-celebration-emoji">🎉</div>
            <div className="demo-celebration-text">{t.shabash}</div>
            <button className="demo-celebration-btn" onClick={onBack}>
              {t.shabashBtn}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoLekhiPage;